import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Badge,
  Flex,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Spinner,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Stack,
  useDisclosure,
  Switch,
  useToast,
} from '@chakra-ui/react';
import { useSearchParams } from 'react-router-dom';
import ReactPaginate from 'react-paginate';
import xhr from 'xhr';
import dayjs from 'dayjs';
import SmartFilterBarTeach from 'components/SmartFilterBarTeach';
import { FiUsers } from 'react-icons/fi';
import { BiGridAlt } from 'react-icons/bi';
import useAuth from 'hooks/useAuth';
import CameraNotesModal from './CameraNotesModal';


const PAGE_SIZE = 50;

function getFullName(student) {
  const u = student?.user || {};
  return `${u.first_name || ''} ${u.last_name || ''}`.trim() || '—';
}

function FiltroInasistenciasStudents() {
  const [searchParams, setSearchParams] = useSearchParams();
  const toast = useToast();
  const [updatingBoletinId, setUpdatingBoletinId] = useState(null);


  const [filters, setFilters] = useState(() => ({
    program_type: searchParams.get('program_type') || '',
    program: searchParams.get('program') || '',
    group: searchParams.get('group') || '',
    semester: searchParams.get('semester') || '',
    status: 'estudiando',
  }));

  const [students, setStudents] = useState([]);
  const [total, setTotal] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(() => {
    const p = searchParams.get('page');
    const n = parseInt(p, 10);
    return n >= 1 ? n : 1;
  });

  const { user } = useAuth();

  const [teacherSubjectIds, setTeacherSubjectIds] = useState([]);
  const [subjectsLoading, setSubjectsLoading] = useState(false);

  const handleToggleBoletin = async (studentId, currentValue) => {
    if (!studentId) return

    try {
      setUpdatingBoletinId(studentId)

      const nextValue = !currentValue

      await xhr.put(`/students/${studentId}`, {
        data: {
          boletin: nextValue,
        },
      })

      setStudents((prev) =>
        prev.map((row) =>
          row?.student?.id === studentId
            ? {
              ...row,
              student: {
                ...row.student,
                boletin: nextValue,
              },
            }
            : row
        )
      )
    } catch (error) {
      console.error('Error actualizando boletín:', error?.response?.data || error)
    } finally {
      setUpdatingBoletinId(null)
    }
  }

  useEffect(() => {
    const loadTeacherSubjects = async () => {


      setSubjectsLoading(true);
      try {
        const url1 = `/subjects?filters[teacher][user][id][$eq]=${user.id}&pagination[pageSize]=200`;


        let res = await xhr(url1);
        let list = res?.data?.data || [];



        if (!list.length && user?.teacher?.id) {
          const url2 = `/subjects?filters[teacher][id][$eq]=${user.teacher.id}&pagination[pageSize]=200`;


          res = await xhr(url2);
          list = res?.data?.data || [];

        }

        const ids = list.map((s) => s?.id).filter(Boolean);



        setTeacherSubjectIds(ids);
      } catch (e) {
        console.error('[subjects] ERROR:', e);
        setTeacherSubjectIds([]);
      } finally {
        setSubjectsLoading(false);
      }
    };

    loadTeacherSubjects();
  }, [user?.id, user?.teacher?.id]);

  const hasMinimumFilters = Boolean(filters.program && filters.group && filters.semester);

  const syncFiltersToUrl = useCallback(
    (nextFilters, page) => {
      const next = new URLSearchParams();

      // guardamos program_type porque lo necesitamos para el backend y para restaurar estado al recargar
      if (nextFilters.program_type) next.set('program_type', nextFilters.program_type);

      if (nextFilters.program) next.set('program', nextFilters.program);
      if (nextFilters.group) next.set('group', nextFilters.group);
      if (nextFilters.semester) next.set('semester', nextFilters.semester);
      if (page > 1) next.set('page', String(page));

      setSearchParams(next, { replace: true });
    },
    [setSearchParams]
  );

  const loadStudents = useCallback(async () => {
    if (!hasMinimumFilters) return;

    try {
      setLoading(true);

      const params = new URLSearchParams({
        program_type: filters.program_type,
        program: filters.program,
        group: filters.group,
        semester: filters.semester,
        status: 'estudiando', // fijo
        include_promotion_pending: 'true',
        page: String(currentPage),
        pageSize: String(PAGE_SIZE),
      });

      const res = await xhr(`/students/generalFilter?${params.toString()}`);
      const data = res.data || res;

      setStudents(data.students || []);
      setTotal(data.total ?? 0);
      setPageCount(data.pageCount ?? 0);
    } catch (err) {
      console.error('Error loading students:', err);
      setStudents([]);
      setTotal(0);
      setPageCount(0);
    } finally {
      setLoading(false);
    }
  }, [hasMinimumFilters, filters, currentPage]);

  useEffect(() => {
    loadStudents();
  }, [loadStudents]);

  const handleFilterChange = useCallback(
    (newFilters) => {
      const forced = { ...newFilters, status: 'estudiando' };
      setFilters(forced);
      setCurrentPage(1);
      syncFiltersToUrl(forced, 1);
    },
    [syncFiltersToUrl]
  );

  const handlePageChange = useCallback(
    (event) => {
      const page = event.selected + 1;
      setCurrentPage(page);
      syncFiltersToUrl(filters, page);
    },
    [filters, syncFiltersToUrl]
  );

  const { isOpen: isAbsOpen, onOpen: onAbsOpen, onClose: onAbsClose } = useDisclosure();
  const [absLoading, setAbsLoading] = useState(false);
  const [absStudent, setAbsStudent] = useState(null);
  const [absences, setAbsences] = useState([]);

  const {
    isOpen: isCameraOpen,
    onOpen: onCameraOpen,
    onClose: onCameraClose,
  } = useDisclosure();

  const [cameraStudent, setCameraStudent] = useState(null);


  const getAttendanceParts = (item) => {
    const a = item?.attributes || item;

    const live = a?.live_class?.data
      ? { id: a.live_class.data.id, ...(a.live_class.data.attributes || {}) }
      : (a?.live_class || null);

    const subject = live?.subject?.data
      ? { id: live.subject.data.id, ...(live.subject.data.attributes || {}) }
      : (live?.subject || null);

    return {
      createdAt: a?.createdAt,
      liveId: live?.id ? Number(live.id) : null,
      liveName: live?.name || live?.title || 'N/A',
      subjectId: subject?.id ? Number(subject.id) : null,
      subjectName: subject?.name || 'N/A',
    };
  };

  const openAbsencesModal = async (studentId, fullName) => {
    if (!studentId) return;

    setAbsStudent({ id: studentId, name: fullName || `Estudiante ${studentId}` });
    setAbsences([]);
    setAbsLoading(true);
    onAbsOpen();

    try {
      if (!teacherSubjectIds?.length) {
        setAbsences([]);
        return;
      }

      // 1) Traer attendances del estudiante 
      const attUrl =
        `/student-attendances` +
        `?filters[student][id][$eq]=${studentId}` +
        `&populate[live_class][populate]=subject` +
        `&pagination[pageSize]=200`;

      const attRes = await xhr(attUrl);
      const attList = attRes?.data?.data || [];

      // Extraer live_class IDs de lo que vino (cubriendo formatos)
      const liveClassIds = Array.from(
        new Set(
          attList
            .map((item) => getAttendanceParts(item).liveId)
            .filter(Boolean)
        )
      );

      if (!liveClassIds.length) {
        setAbsences([]);
        return;
      }


      const allowed = new Set(teacherSubjectIds.map(Number));

      // 2.5) Consultar excusas del estudiante para esas live_classes
      const excuseUrl =
        `/student-excuses` +
        `?filters[student][id][$eq]=${studentId}` +
        `&populate=live_classes` +
        `&pagination[pageSize]=200`;

      const excuseRes = await xhr(excuseUrl);
      const excuseList = excuseRes?.data?.data || [];

      // Set con live_class IDs que tienen excusa
      const excusedLiveClassIds = new Set();

      const liveSet = new Set(liveClassIds.map(Number));
      excuseList.forEach((item) => {
        const a = item?.attributes || item;
        const lives = a?.live_classes?.data || a?.live_classes || [];

        lives.forEach((lc) => {
          const id = lc?.id || lc?.data?.id;
          const n = Number(id);
          if (id && liveSet.has(n)) excusedLiveClassIds.add(n);
        });
      });



      // 3) Filtrar attendances por subjects del docente
      const filtered = attList.filter((item) => {
        const { subjectId } = getAttendanceParts(item);
        return subjectId && allowed.has(subjectId);
      });

      // 4) Pintar resultado final
      setAbsences(
        filtered.map((item) => {
          const a = item?.attributes || item;
          const parts = getAttendanceParts(item);

          return {
            id: item?.id || a?.id,
            createdAt: parts.createdAt,
            liveClassId: parts.liveId,
            liveClassName: parts.liveName,
            subjectName: parts.subjectName,
            isExcused: parts.liveId ? excusedLiveClassIds.has(Number(parts.liveId)) : false,
          };
        })
      );
    } catch (e) {
      console.error('Error cargando inasistencias (2 requests):', e);
      setAbsences([]);
    } finally {
      setAbsLoading(false);
    }
  };


  const start = total === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const end = Math.min(currentPage * PAGE_SIZE, total);

  const tableThProps = {
    bg: 'gray.50',
    py: 3,
    px: 2,
    fontSize: 'xs',
    fontWeight: 'semibold',
    color: 'gray.700',
    borderColor: 'gray.100',
  };
  function getInitials(student) {
    const u = student?.user || {};
    const first = (u.first_name || '').trim().charAt(0) || '';
    const last = (u.last_name || '').trim().charAt(0) || '';
    return (first + last).toUpperCase() || '?';
  }

  const openCameraNotesModal = (studentId, fullName) => {
    if (!studentId) return;

    setCameraStudent({
      id: studentId,
      name: fullName || `Estudiante ${studentId}`,
    });

    onCameraOpen();
  };

  return (
    <Box p={4} maxW="100%">
      <SmartFilterBarTeach
        title="Filtro Estudiantes"
        description="Selecciona Grado, Grupo (obligatorio) y Semestre. Estado se aplica automáticamente como 'Estudiando'."
        value={filters}
        onChange={handleFilterChange}
      />

      {!hasMinimumFilters && (
        <Box mt={6} p={6} borderRadius="xl" borderWidth="1px" borderColor="gray.200" bg="gray.50">
          <Text color="gray.700" fontWeight="semibold" mb={1}>
            Faltan filtros obligatorios
          </Text>
          <Text color="gray.600" fontSize="sm">
            Debes seleccionar <b>Grado</b>, <b>Grupo</b> y <b>Semestre</b>.
          </Text>
        </Box>
      )}

      {hasMinimumFilters && (
        <>
          <Flex align="center" justify="space-between" flexWrap="wrap" gap={3} my={4}>
            <Flex align="center" gap={2}>
              <Box bg="blue.100" p={2} borderRadius="full">
                <Box as={FiUsers} color="blue.600" boxSize={5} />
              </Box>
              <Text fontWeight="bold" fontSize="lg">
                {total}
              </Text>
              <Text fontSize="sm" color="gray.600">
                estudiantes
              </Text>
            </Flex>
          </Flex>

          <Box bg="white" borderRadius="lg" boxShadow="sm" overflow="hidden" borderWidth="1px" borderColor="gray.100">
            <Box bg="#574feb" px={5} py={3}>
              <Flex align="center" gap={3}>
                <Box as={BiGridAlt} color="white" boxSize={6} />
                <Text fontWeight="bold" color="white" fontSize="lg">
                  Listado de estudiantes
                </Text>
              </Flex>
            </Box>

            {loading ? (
              <Flex justify="center" py={12}>
                <Spinner size="lg" />
              </Flex>
            ) : (
              <TableContainer overflowX="auto" maxW="100%">
                <Table size="sm" variant="simple">
                  <Thead>
                    <Tr>
                      <Th {...tableThProps} minW="220px">
                        ESTUDIANTE
                      </Th>
                      <Th {...tableThProps}>CÓDIGO</Th>
                      <Th {...tableThProps}>BOLETÍN</Th>
                      <Th {...tableThProps}>INASIST.</Th>
                      <Th {...tableThProps}>CÁMARA</Th>
                    </Tr>
                  </Thead>

                  <Tbody>
                    {students.length === 0 ? (
                      <Tr>
                        <Td colSpan={5} py={8} textAlign="center" color="gray.500">
                          No hay estudiantes para los filtros seleccionados.
                        </Td>
                      </Tr>
                    ) : (
                      students.map((row) => {
                        const st = row.student;
                        const fullName = getFullName(st);

                        return (
                          <Tr key={row.id}>
                            <Td py={2} px={3}>
                              <Flex align="center" gap={3}>
                                <Flex
                                  w={10}
                                  h={10}
                                  borderRadius="full"
                                  bg="#E8E4FC"
                                  color="#574feb"
                                  borderWidth="1px"
                                  borderColor="#D4CCF0"
                                  align="center"
                                  justifyContent="center"
                                  fontSize="xs"
                                  fontWeight="bold"
                                >
                                  {getInitials(st)}
                                </Flex>
                                <Box>
                                  <Text fontWeight="medium" color="gray.800">
                                    {fullName}
                                  </Text>
                                  <Text fontSize="xs" color="gray.500">
                                    ID: {st?.id ?? '—'}
                                  </Text>
                                </Box>
                              </Flex>
                            </Td>

                            <Td py={2} px={2}>
                              {st?.id ?? '—'}
                            </Td>

                            <Td py={2} px={2}>
                              <Switch
                                isChecked={Boolean(st?.boletin)}
                                onChange={() => handleToggleBoletin(st?.id, st?.boletin)}
                                isDisabled={updatingBoletinId === st?.id}
                                colorScheme={st?.boletin ? 'green' : 'gray'}
                              />
                            </Td>

                            <Td py={2} px={2}>
                              <Button size="xs" onClick={() => openAbsencesModal(st?.id, fullName)}>
                                Inasistencias
                              </Button>
                            </Td>
                            <Td py={2} px={2}>
                              <Button
                                size="xs"
                                colorScheme="purple"
                                onClick={() => openCameraNotesModal(st?.id, fullName)}
                                isDisabled={subjectsLoading || !teacherSubjectIds?.length}
                              >
                                Ver notas cámara
                              </Button>
                            </Td>
                          </Tr>
                        );
                      })
                    )}
                  </Tbody>
                </Table>
              </TableContainer>
            )}

            <Flex
              justify="space-between"
              align="center"
              p={4}
              borderTopWidth="1px"
              borderColor="gray.100"
              flexWrap="wrap"
              gap={3}
            >
              <Text color="gray.600" fontSize="sm">
                Mostrando {start}-{end} de {total} estudiantes
              </Text>

              {pageCount > 1 && (
                <ReactPaginate
                  breakLabel="..."
                  nextLabel=">"
                  previousLabel="<"
                  onPageChange={handlePageChange}
                  pageRangeDisplayed={3}
                  marginPagesDisplayed={1}
                  pageCount={pageCount}
                  forcePage={currentPage - 1}
                  renderOnZeroPageCount={null}
                  containerClassName="filtroStudentsPagination"
                  pageClassName="filtroStudentsPaginationItem"
                  activeClassName="filtroStudentsPaginationActive"
                  previousClassName="filtroStudentsPaginationItem"
                  nextClassName="filtroStudentsPaginationItem"
                  disabledClassName="filtroStudentsPaginationDisabled"
                />
              )}
            </Flex>
          </Box>

          <style>{`
          .filtroStudentsPagination {
            display: flex;
            list-style: none;
            gap: 4px;
            margin: 0;
            padding: 0;
          }
          .filtroStudentsPaginationItem {
            display: flex;
            align-items: center;
            justify-content: center;
            min-width: 36px;
            height: 36px;
            border: 1px solid #e2e8f0;
            border-radius: 6px;
            cursor: pointer;
            font-size: 14px;
            color: #4a5568;
            transition: all 0.2s;
          }
          .filtroStudentsPaginationItem:hover {
            background-color: #f7fafc;
            border-color: #cbd5e0;
          }
          .filtroStudentsPaginationItem a {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 100%;
            height: 100%;
            padding: 0 12px;
          }
          .filtroStudentsPaginationActive {
            background-color: #574feb !important;
            border-color: #574feb !important;
            color: white !important;
          }
          .filtroStudentsPaginationActive a {
            color: white !important;
          }
          .filtroStudentsPaginationDisabled {
            opacity: 0.5;
            cursor: not-allowed;
          }
        `}</style>

        </>
      )}
      <Modal isOpen={isAbsOpen} onClose={onAbsClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Inasistencias {absStudent?.name ? `- ${absStudent.name}` : ''}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody fontSize="sm">
            {absLoading ? (
              <Flex justify="center" py={6}>
                <Spinner />
              </Flex>
            ) : !absences?.length ? (
              <Text>No hay inasistencias registradas.</Text>
            ) : (
              <Stack spacing={3}>
                {absences.map((a) => (
                  <Box key={a.id} p={3} bg="gray.50" rounded="md">
                    <Flex justify="space-between" align="center" mb={1}>
                      <Text>
                        <b>Fecha:</b> {a.createdAt ? dayjs(a.createdAt).format('YYYY-MM-DD HH:mm') : 'N/A'}
                      </Text>

                      {a.isExcused && (
                        <Badge colorScheme="green" borderRadius="full" px={2} py={1} fontSize="xs">
                          Excusada
                        </Badge>
                      )}
                    </Flex>

                    <Text mt={1}>
                      <b>Clase:</b> {a.liveClassName || 'N/A'}
                    </Text>
                    <Text mt={1}>
                      <b>Materia:</b> {a.subjectName || 'N/A'}
                    </Text>
                  </Box>
                ))}
              </Stack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={onAbsClose}>Cerrar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <CameraNotesModal
        isOpen={isCameraOpen}
        onClose={onCameraClose}
        student={cameraStudent}
        gradeId={filters.program}
        teacherSubjectIds={teacherSubjectIds}
      />
    </Box>
  );
}

export default FiltroInasistenciasStudents;